import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody } from '../../components/card/card.jsx';
import useAuth from '../../hooks/useAuth.js';
import { useCronTasks } from '../../hooks/useCronTasks.js';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';
import toastService from '../../services/toastService.js';
import CronTaskForm from './components/CronTaskForm.jsx';
import CronTaskList from './components/CronTaskList.jsx';
import CronExecutionHistory from './components/CronExecutionHistory.jsx';

/**
 * Cron Tests page component for managing scheduled security scans
 * Available for superuser and admin roles
 */
const CronTests = () => {
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewingExecutions, setViewingExecutions] = useState(null);
  const [viewingResults, setViewingResults] = useState(null);
  
  // Custom confirm dialog
  const { showConfirm, dialogState } = useConfirmDialog();

  // Check user permissions
  const userRole = user?.role || 'user';
  const isAdmin = userRole === 'admin';
  const isSuperUser = userRole === 'superuser' || isAdmin;

  // Use cron tasks hook
  const {
    tasks,
    loading,
    error,
    reload,
    createTask,
    updateTask,
    deleteTask,
    toggleTask,
    triggerTask
  } = useCronTasks();

  useEffect(() => {
    if (!isSuperUser) {
      toastService.error('Access denied. This feature requires superuser or admin privileges.');
      return;
    }
    
    // Load cron tasks
    reload();
  }, [isSuperUser, reload]);

  // Calculate statistics
  const activeTasks = tasks.filter(task => task.isEnabled).length;
  const nextRunTask = tasks
    .filter(task => task.isEnabled && task.nextRun)
    .sort((a, b) => new Date(a.nextRun) - new Date(b.nextRun))[0];
  const executionsToday = tasks.reduce((sum, task) => {
    const today = new Date().toDateString();
    return sum + (task.executionCount && task.lastExecution && 
      new Date(task.lastExecution.startTime).toDateString() === today ? 1 : 0);
  }, 0);

  // Handle form submission
  const handleFormSubmit = async (taskData) => {
    try {
      if (editingTask) {
        await updateTask(editingTask.id, taskData);
        toastService.success('Cron task updated successfully');
        setEditingTask(null);
      } else {
        await createTask(taskData);
        toastService.success('Cron task created successfully');
        setShowCreateForm(false);
      }
    } catch (error) {
      console.error('Error saving task:', error);
      toastService.error(editingTask ? 'Failed to update task' : 'Failed to create task');
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowCreateForm(false);
    setEditingTask(null);
  };

  // Handle task actions
  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowCreateForm(true);
  };

  const handleDeleteTask = async (taskId) => {
    const confirmed = await showConfirm({
      title: 'Delete Cron Task',
      message: 'Are you sure you want to delete this cron task? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger'
    });
    
    if (confirmed) {
      try {
        await deleteTask(taskId);
        toastService.success('Cron task deleted successfully');
      } catch (error) {
        console.error('Error deleting task:', error);
        toastService.error('Failed to delete task');
      }
    }
  };

  const handleToggleTask = async (taskId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        toastService.error('Task not found');
        return;
      }
      
      const newEnabledState = !task.isEnabled;
      await toggleTask(taskId, newEnabledState);
      toastService.success('Task status updated successfully');
    } catch (error) {
      console.error('Error toggling task:', error);
      toastService.error('Failed to update task status');
    }
  };

  const handleTriggerTask = async (taskId) => {
    try {
      await triggerTask(taskId);
      toastService.success('Task triggered successfully');
    } catch (error) {
      console.error('Error triggering task:', error);
      toastService.error('Failed to trigger task');
    }
  };

  const handleViewExecutions = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    setViewingExecutions({ taskId, taskName: task?.name || 'Unknown Task' });
  };

  const handleViewResults = (execution) => {
    setViewingResults(execution);
  };

  if (!isSuperUser) {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <Card>
              <CardHeader>
                <h4 className="mb-0">
                  <i className="bi bi-exclamation-triangle me-2 text-warning"></i>
                  Access Denied
                </h4>
              </CardHeader>
              <CardBody>
                <p className="text-muted">
                  This feature is only available for superuser and admin roles.
                  Please contact your administrator if you need access to scheduled testing features.
                </p>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Show execution history view
  if (viewingExecutions) {
    return (
      <div className="container-fluid">
        <CronExecutionHistory
          taskId={viewingExecutions.taskId}
          taskName={viewingExecutions.taskName}
          onClose={() => setViewingExecutions(null)}
          onViewResults={handleViewResults}
        />
      </div>
    );
  }

  // Show create/edit form
  if (showCreateForm) {
    return (
      <div className="container-fluid">
        <CronTaskForm
          task={editingTask}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={loading}
          userRole={userRole}
        />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <i className="bi bi-clock-history me-2"></i>
                Cron Tests
              </h2>
              <p className="text-muted mb-0">
                Schedule and manage automated security scans
              </p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
              disabled={loading}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Create New Task
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col">
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {error}
              <button 
                className="btn btn-sm btn-outline-danger ms-3"
                onClick={reload}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Statistics */}
      <div className="row mb-4">
        <div className="col-md-3">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-list-task fs-2 text-primary"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold">Total Tasks</div>
                  <div className="text-muted">{tasks.length}</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-3">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-play-circle fs-2 text-success"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold">Active Tasks</div>
                  <div className="text-muted">{activeTasks}</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-3">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-clock fs-2 text-warning"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold">Next Run</div>
                  <div className="text-muted">
                    {nextRunTask ? (
                      <span title={nextRunTask.name}>
                        {new Date(nextRunTask.nextRun).toLocaleString()}
                      </span>
                    ) : '-'}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-3">
          <Card>
            <CardBody>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <i className="bi bi-graph-up fs-2 text-info"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <div className="fw-bold">Executions Today</div>
                  <div className="text-muted">{executionsToday}</div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Tasks List */}
      <div className="row">
        <div className="col">
          <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="bi bi-list me-2"></i>
                  Scheduled Tasks
                </h5>
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-sm btn-outline-secondary"
                    onClick={reload}
                    disabled={loading}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Refresh
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <CronTaskList
                tasks={tasks}
                onEdit={handleEditTask}
                onDelete={handleDeleteTask}
                onToggle={handleToggleTask}
                onTrigger={handleTriggerTask}
                onViewExecutions={handleViewExecutions}
                isLoading={loading}
                userRole={userRole}
              />
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Results Modal */}
      {viewingResults && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Scan Results - {viewingResults.id}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setViewingResults(null)}
                ></button>
              </div>
              <div className="modal-body">
                {/* This would integrate with the scan results component from small-pentest */}
                <div className="alert alert-info">
                  <i className="bi bi-info-circle me-2"></i>
                  Scan results viewer integration with Small Pentest module coming soon.
                </div>
                <pre className="bg-light p-3 rounded">
                  {JSON.stringify(viewingResults.results, null, 2)}
                </pre>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setViewingResults(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom Confirm Dialog */}
      <ConfirmDialog
        isVisible={dialogState.isVisible}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.confirmText}
        cancelText={dialogState.cancelText}
        confirmVariant={dialogState.confirmVariant}
        size={dialogState.size}
        onConfirm={dialogState.onConfirm}
        onCancel={dialogState.onCancel}
      />
    </div>
  );
};

export default CronTests;